import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { GoogleMap, Polyline as GooglePolyline } from '@react-google-maps/api';
import polyline from '@mapbox/polyline';
import { API_BASE_URL } from '../../config';
import './GroupRideRouteDisplay.css';

interface GroupRideRouteDisplayProps {
  rideId: number;
  routeSource: 'strava' | 'gpx' | 'manual' | 'fit' | null;
  routeDistance: number | null;
  routeElevationGain: number | null;
  routePolyline: string | null;
  stravaRouteUrl: string | null;
  canDownloadGpx: boolean;
  isLoaded: boolean;
  endLocationName?: string | null;
  endLocationLat?: number | null;
  endLocationLng?: number | null;
}

const GroupRideRouteDisplay: React.FC<GroupRideRouteDisplayProps> = ({
  rideId,
  routeSource,
  routeDistance,
  routeElevationGain,
  routePolyline,
  stravaRouteUrl,
  canDownloadGpx,
  isLoaded,
  endLocationName,
}) => {
  const { t } = useLanguage();

  if (!routeSource) {
    return (
      <div className="group-ride-route">
        <div className="group-ride-route-empty">
          {t('groupRideRoute.noRoute')}
        </div>
      </div>
    );
  }

  const getSourceLabel = (): string => {
    switch (routeSource) {
      case 'strava':
        return t('groupRideRoute.sourceStrava');
      case 'gpx':
        return t('groupRideRoute.sourceGpx');
      case 'manual':
        return t('groupRideRoute.sourceManual');
      case 'fit':
        return t('groupRideRoute.sourceFit');
      default:
        return '';
    }
  };

  const formatDistance = (meters: number): string => {
    return (meters / 1000).toFixed(1) + ' km';
  };

  const formatElevation = (meters: number): string => {
    return Math.round(meters) + ' m';
  };

  const renderMap = () => {
    if (!isLoaded || !routePolyline) return null;

    let path: { lat: number; lng: number }[] = [];
    try {
      const decoded: number[][] = polyline.decode(routePolyline);
      if (!decoded || decoded.length < 2) return null;
      path = decoded
        .filter((point: number[]) => {
          if (!Array.isArray(point) || point.length < 2) return false;
          const [lat, lng] = point;
          return (
            typeof lat === 'number' &&
            typeof lng === 'number' &&
            !isNaN(lat) &&
            !isNaN(lng) &&
            lat >= -90 &&
            lat <= 90 &&
            lng >= -180 &&
            lng <= 180
          );
        })
        .map((point: number[]) => ({ lat: point[0], lng: point[1] }));
    } catch (error) {
      console.error('Error decoding route polyline:', error);
      return null;
    }

    if (path.length < 2) return null;
    const center = path[Math.floor(path.length / 2)];

    return (
      <div className="group-ride-route-map">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={center}
          zoom={12}
          options={{ disableDefaultUI: true, gestureHandling: 'none' }}
        >
          <GooglePolyline
            key={`route-poly-${rideId}`}
            path={path}
            options={{ strokeColor: '#6b7280', strokeWeight: 3 }}
          />
        </GoogleMap>
      </div>
    );
  };

  return (
    <div className="group-ride-route">
      <div className="group-ride-route-header">
        <span className="group-ride-route-title">
          {t('groupRideRoute.route')}
        </span>
        <span className="group-ride-route-source-badge">
          {getSourceLabel()}
        </span>
      </div>

      {renderMap()}

      {(routeDistance || routeElevationGain) && (
        <div className="group-ride-route-stats">
          {routeDistance != null && routeDistance > 0 && (
            <div className="group-ride-route-stat">
              <span className="group-ride-route-stat-value">
                {formatDistance(routeDistance)}
              </span>
              <span className="group-ride-route-stat-label">
                {t('groupRideRoute.distance')}
              </span>
            </div>
          )}
          {routeElevationGain != null && routeElevationGain > 0 && (
            <div className="group-ride-route-stat">
              <span className="group-ride-route-stat-value">
                {formatElevation(routeElevationGain)}
              </span>
              <span className="group-ride-route-stat-label">
                {t('groupRideRoute.elevation')}
              </span>
            </div>
          )}
        </div>
      )}

      {(stravaRouteUrl || canDownloadGpx) && (
        <div className="group-ride-route-actions">
          {stravaRouteUrl && (
            <a
              href={stravaRouteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group-ride-route-btn"
            >
              <span className="group-ride-route-btn-icon">&#x1F6B4;</span>
              {t('groupRideRoute.viewOnStrava')}
            </a>
          )}
          {canDownloadGpx && (
            <a
              href={`${API_BASE_URL}/api/mobile/group-rides/${rideId}/route/gpx`}
              download
              className="group-ride-route-btn"
            >
              <span className="group-ride-route-btn-icon">&#x2B07;</span>
              {t('groupRideRoute.downloadGpx')}
            </a>
          )}
        </div>
      )}

      {endLocationName && (
        <div className="group-ride-route-end-location">
          <span className="group-ride-route-end-icon">&#x1F3C1;</span>
          <span className="group-ride-route-end-label">{t('groupRideRoute.endLocation')}:</span>
          <span className="group-ride-route-end-name">{endLocationName}</span>
        </div>
      )}
    </div>
  );
};

export default GroupRideRouteDisplay;
